/**
 * Ownership registry for the single right-sidebar content slot held by shared-ui's
 * `RightSidebarContentProvider`.
 *
 * `RightSidebarRenderer` renders that node outside `<Routes>`, so it outlives the
 * `PageContent` that stored it: without an explicit release, an unmounted page's sidebar
 * keeps rendering (this is how a chat `ConversationSidebar` survived its own route and
 * called `useConversationHub()` with no `ConversationHubProvider` above it).
 *
 * So each page releases the slot on unmount — but only while it still owns it. React may
 * run the outgoing tree's passive cleanup either before or after the incoming tree's
 * effects (a lazy/Suspense boundary splits them across commits), so the current owner has
 * to be read live at cleanup time. A value captured in a closure or a render snapshot can
 * be one navigation stale, and clearing on a stale read would blank the sidebar the
 * incoming page just set.
 *
 * Invariant: every write to the slot claims it, so the owner is always the last writer.
 *
 * Deliberately a plain module rather than a Zustand store (cf. `enabled-features.store.ts`):
 * nothing renders from this value, and putting it in `sidebar.store` would re-render every
 * `useSidebarStore()` consumer on each navigation.
 */

let ownerId: string | null = null;

/** Records `candidateId` as the owner of the right-sidebar content slot. */
export const claimRightSidebarContent = (candidateId: string): void => {
  ownerId = candidateId;
};

/**
 * Releases the slot if `candidateId` still owns it.
 *
 * @returns `true` when ownership was held and has now been released — the caller should
 * clear the stored content. `false` means another page has taken the slot over; leave its
 * content alone.
 */
export const releaseRightSidebarContent = (candidateId: string): boolean => {
  if (ownerId !== candidateId) {
    return false;
  }

  ownerId = null;
  return true;
};

/** Current owner of the slot, or `null` when nobody owns it. */
export const getRightSidebarContentOwner = (): string | null => ownerId;
